/**
 * main-product-v9.js
 *
 * Product V9 is a single-product PDP section with a 3-offer accordion buy box.
 * A single state drives everything: data-active-offer on .productV9BuyBox
 * (twelve | family | onetime).
 *   twelve  → subscription card; the selling_plan_offer frequency dropdown
 *             switches plans and repaints prices/labels.
 *   family  → subscription-only bundle: two lines (adult + kids pouch)
 *             resolved from the card's internal flavor selects, both added
 *             with the family selling plan (mini-cart _family_pack contract).
 *   onetime → bare row tracking the twelve dropdown's one-time price.
 */

(function () {
  var scriptTag = document.currentScript;
  var sectionId = scriptTag ? scriptTag.dataset.id : null;

  function getSection() {
    return sectionId ? document.getElementById(sectionId) : document;
  }

  function fmtMoney(cents) {
    if (cents == null) return '';
    var val = cents / 100;
    return '$' + (val % 1 === 0 ? val.toFixed(0) : val.toFixed(2));
  }

  function parseProductJSON(section) {
    var el = section.querySelector('.productV9JSON');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function getActiveOffer(section) {
    var select = section.querySelector('.productV9OfferSelect');
    if (!select) return null;
    return select.selectedOptions[0] || select.options[0] || null;
  }

  function getPlanAllocation(variant, offer) {
    if (!variant || !offer || !variant.selling_plan_allocations) return null;
    var planId = String(offer.value || '');
    return variant.selling_plan_allocations.find(function (allocation) {
      return String(allocation.selling_plan_id) === planId;
    }) || null;
  }

  function setText(el, value) {
    if (el) el.textContent = value || '';
  }

  function setHtml(el, value) {
    if (el) el.innerHTML = value || '';
  }

  function firstLine(value) {
    return String(value || '').split('\n')[0];
  }

  /* ── Accordion state (lives in the DOM, no parallel JS state) ── */

  function getBuyBox(section) {
    return section.querySelector('.productV9BuyBox');
  }

  function getActiveOfferKey(section) {
    var buyBox = getBuyBox(section);
    return (buyBox && buyBox.dataset.activeOffer) || 'twelve';
  }

  function getOfferCard(section, key) {
    return section.querySelector('.productV9Offer[data-offer="' + key + '"]');
  }

  function getCurrentVariant(section) {
    var productData = parseProductJSON(section);
    if (!productData || !productData.variants) return null;
    var variantInput = section.querySelector('.productV9VariantInput');
    var currentId = variantInput ? parseInt(variantInput.value, 10) : null;
    return productData.variants.find(function (item) { return item.id === currentId; }) || null;
  }

  function initGallerySwiper(section) {
    if (typeof Swiper === 'undefined') return;
    section.querySelectorAll('.productVisualSwiperGroupItem').forEach(function (el) {
      var swiperEl = el.querySelector('.productVisualSwiper');
      if (!swiperEl) return;

      var instance = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        autoHeight: true,
        pagination: {
          el: swiperEl.querySelector('.swiper-pagination'),
          clickable: true,
        },
        on: {
          slideChange: function () {
            var thumbs = el.querySelectorAll('.productVisualThumbsSlide');
            thumbs.forEach(function (thumb) { thumb.classList.remove('active'); });
            if (thumbs[this.realIndex]) thumbs[this.realIndex].classList.add('active');
          }
        }
      });

      el.querySelectorAll('.productVisualThumbsSlide').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          instance.slideTo(parseInt(this.dataset.index, 10) - 1);
        });
      });
    });
  }

  function switchGallery(section, id) {
    section.querySelectorAll('.productVisualSwiperGroupItem').forEach(function (el) {
      el.classList.toggle('active', el.dataset.id === id);
    });
    /* VAR A (Intelligems A/B gallery) mirrors the same data-id contract.
       Broadcasting lets main-product-v9-var-a.js activate its group and
       call .update() after the class flip, without this file knowing
       anything about VAR A. Remove with the experiment. */
    section.dispatchEvent(new CustomEvent('productV9:gallerySwitch', {
      detail: { id: id }
    }));
  }

  function resolveVariant(productData, flavorValue) {
    if (!productData || !productData.variants) return null;
    return productData.variants.find(function (variant) {
      return variant.options.indexOf(flavorValue) !== -1;
    }) || null;
  }

  function applyOfferAvailability(section, variant) {
    var select = section.querySelector('.productV9OfferSelect');
    if (!select) return;

    var allocations = variant && variant.selling_plan_allocations ? variant.selling_plan_allocations : [];
    var availablePlanIds = allocations.map(function (allocation) {
      return String(allocation.selling_plan_id);
    });
    if (!availablePlanIds.length) return;

    var needsReselect = false;
    Array.prototype.forEach.call(select.options, function (option) {
      var isAvailable = availablePlanIds.indexOf(String(option.value)) !== -1;
      option.disabled = !isAvailable;
      if (!isAvailable && option.selected) {
        option.selected = false;
        needsReselect = true;
      }
    });

    if (needsReselect) {
      var firstAvailable = Array.prototype.find.call(select.options, function (option) {
        return !option.disabled;
      });
      if (firstAvailable) firstAvailable.selected = true;
    }
  }

  /* Repaints everything that tracks the twelve frequency dropdown: the twelve
     card (expanded header, price row, promo badge) and its collapsed row, plus
     the one-time row. Queries are scoped to the twelve card because the family
     card reuses the same SubBox* classes. Ends by recomposing the CTA. */
  function applyOfferTexts(section, variant) {
    var offer = getActiveOffer(section);
    var allocation = getPlanAllocation(variant, offer);
    var twelveCard = getOfferCard(section, 'twelve') || section;

    var subPriceText = offer ? (offer.dataset.subscriptionPriceText || '') : '';
    var subCadence = offer ? (offer.dataset.subscriptionCadence || '') : '';
    var otpPriceText = offer ? (offer.dataset.otpPriceText || '') : '';
    var discountLabel = offer ? (offer.dataset.discountLabel || '') : '';
    var servingsDescription = offer ? (offer.dataset.servingsDescription || '') : '';
    var otpSupplyText = offer ? (offer.dataset.otpSupplyText || '') : '';
    var offerLabel = offer ? (offer.dataset.label || '') : '';

    if (!subPriceText) subPriceText = allocation ? fmtMoney(allocation.price) : fmtMoney(variant && variant.price);
    if (!otpPriceText) otpPriceText = fmtMoney(variant && variant.price);

    /* Twelve card — expanded body */
    var subPriceEl = twelveCard.querySelector('.productV9SubPrice');
    if (subPriceEl) subPriceEl.textContent = subPriceText;

    var subBoxPricePerEl = twelveCard.querySelector('.productV9SubBoxPricePer');
    if (subBoxPricePerEl) {
      if (subCadence) {
        subBoxPricePerEl.textContent = '/' + subCadence;
        subBoxPricePerEl.style.display = '';
      } else {
        subBoxPricePerEl.style.display = 'none';
      }
    }
    var subBoxCompareEl = twelveCard.querySelector('.productV9SubBoxComparePrice');
    if (subBoxCompareEl) {
      if (otpPriceText) {
        subBoxCompareEl.textContent = otpPriceText;
        subBoxCompareEl.style.display = '';
      } else {
        subBoxCompareEl.style.display = 'none';
      }
    }
    var subBoxTitleEl = twelveCard.querySelector('.productV9SubBoxHeaderTitle');
    if (subBoxTitleEl && offerLabel) subBoxTitleEl.textContent = offerLabel;
    setHtml(twelveCard.querySelector('.productV9SubBoxHeaderServings'), servingsDescription);

    var promoBadgeEl = twelveCard.querySelector('.productV9SubBoxPromoBadge');
    if (promoBadgeEl) {
      var tpl = promoBadgeEl.dataset.template || promoBadgeEl.textContent || '';
      if (!promoBadgeEl.dataset.template) promoBadgeEl.dataset.template = tpl;
      if (discountLabel) {
        promoBadgeEl.textContent = promoBadgeEl.dataset.template.replace('{discount}', discountLabel);
        promoBadgeEl.style.display = '';
      } else {
        promoBadgeEl.style.display = 'none';
      }
    }

    /* Twelve card — collapsed row */
    if (offerLabel) setText(section.querySelector('.productV9TwelveCollapsedTitle'), offerLabel);
    var collapsedPillEl = section.querySelector('.productV9TwelveCollapsedPill');
    if (collapsedPillEl) {
      if (discountLabel) {
        collapsedPillEl.textContent = discountLabel + ' OFF';
        collapsedPillEl.style.display = '';
      } else {
        collapsedPillEl.style.display = 'none';
      }
    }
    setHtml(section.querySelector('.productV9TwelveCollapsedServings'), servingsDescription);
    setText(section.querySelector('.productV9TwelveCollapsedAmount'), subPriceText);
    var collapsedCompareEl = section.querySelector('.productV9TwelveCollapsedCompare');
    if (collapsedCompareEl) {
      if (otpPriceText) {
        collapsedCompareEl.textContent = otpPriceText;
        collapsedCompareEl.style.display = '';
      } else {
        collapsedCompareEl.style.display = 'none';
      }
    }
    setText(section.querySelector('.productV9TwelveCollapsedSupply'), firstLine(otpSupplyText));

    /* One-time row tracks the configured plan */
    var onetimeTitleEl = section.querySelector('.productV9OnetimeTitle');
    if (onetimeTitleEl) {
      var titleTemplate = onetimeTitleEl.dataset.template || 'One-Time Purchase - {label}';
      onetimeTitleEl.textContent = titleTemplate.replace('{label}', offerLabel);
    }
    setText(section.querySelector('.productV9OnetimeAmount'), otpPriceText);
    setText(section.querySelector('.productV9OnetimeSupply'), firstLine(otpSupplyText));

    updateCta(section);
  }

  /* Top-of-page price block (.productV9PriceBlock) follows the ACTIVE offer:
     twelve → subscription price/cadence, family → authored bundle price,
     onetime → the plan's one-time price. */
  function applyTopPrice(section, variant) {
    var offerKey = getActiveOfferKey(section);
    var offer = getActiveOffer(section);
    var allocation = getPlanAllocation(variant, offer);
    var familyCard = getOfferCard(section, 'family');

    var subPriceText = offer ? (offer.dataset.subscriptionPriceText || '') : '';
    var subCadence = offer ? (offer.dataset.subscriptionCadence || '') : '';
    var otpPriceText = offer ? (offer.dataset.otpPriceText || '') : '';
    var discountLabel = offer ? (offer.dataset.discountLabel || '') : '';
    var servingsPer = offer ? (offer.dataset.servingsPer || '') : '';
    if (!subPriceText) subPriceText = allocation ? fmtMoney(allocation.price) : fmtMoney(variant && variant.price);
    if (!otpPriceText) otpPriceText = fmtMoney(variant && variant.price);

    var price = subPriceText;
    var per = subCadence;
    var compare = otpPriceText;
    var save = discountLabel;

    if (offerKey === 'family' && familyCard) {
      price = familyCard.dataset.ctaPrice || price;
      compare = familyCard.dataset.comparePrice || '';
      per = '';
      save = familyCard.dataset.discountLabel || '';
    } else if (offerKey === 'onetime') {
      price = otpPriceText;
      per = '';
      compare = '';
      save = '';
    }

    if (!compare && variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
      compare = fmtMoney(variant.compare_at_price);
    }
    if (compare && compare === price) compare = '';

    setText(section.querySelector('.productV9Price'), price);

    var pricePerEl = section.querySelector('.productV9PricePer');
    if (pricePerEl) {
      if (per) {
        pricePerEl.textContent = '/' + per;
        pricePerEl.style.display = '';
      } else {
        pricePerEl.style.display = 'none';
      }
    }

    var comparePriceEl = section.querySelector('.productV9ComparePrice');
    if (comparePriceEl) {
      if (compare) {
        comparePriceEl.textContent = compare;
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    var saveBadgeEl = section.querySelector('.productV9PriceSaveBadge');
    if (saveBadgeEl) {
      if (save) {
        saveBadgeEl.textContent = 'Save ' + save;
        saveBadgeEl.style.display = '';
      } else {
        saveBadgeEl.style.display = 'none';
      }
    }

    var badgeEl = section.querySelector('.productV9PriceBadge');
    if (badgeEl && servingsPer) badgeEl.textContent = 'As low as ' + servingsPer + ' a day';
  }

  /* CTA always names the real price: "Add to Cart · {price}" */
  function updateCta(section) {
    var atcBtn = section.querySelector('.productV9Atc');
    if (!atcBtn) return;
    var textEl = atcBtn.querySelector('.productV9AtcText');
    var offerKey = getActiveOfferKey(section);
    var offer = getActiveOffer(section);
    var available = atcBtn.dataset.available !== 'false';

    var price = '';
    if (offerKey === 'family') {
      var familyCard = getOfferCard(section, 'family');
      price = (familyCard && familyCard.dataset.ctaPrice) || '';
      /* Family availability is guaranteed by Liquid: the card only renders
         with both slots resolvable, independent of the page variant. */
      available = Boolean(familyCard);
    } else if (offerKey === 'onetime') {
      price = offer ? (offer.dataset.otpPriceText || '') : '';
    } else {
      price = offer ? (offer.dataset.subscriptionPriceText || '') : '';
    }

    atcBtn.disabled = !available;
    if (textEl) {
      textEl.textContent = available
        ? 'Add to Cart' + (price ? ' · ' + price : '')
        : 'Sold Out';
    }

    var atcPriceEl = section.querySelector('.productV9SubAtcPrice');
    if (atcPriceEl) atcPriceEl.textContent = price;
  }

  function setActiveOffer(section, key) {
    var buyBox = getBuyBox(section);
    var card = getOfferCard(section, key);
    if (!buyBox || !card) return;
    if (buyBox.dataset.activeOffer === key) return;

    buyBox.dataset.activeOffer = key;
    section.querySelectorAll('.productV9Offer').forEach(function (item) {
      var isActive = item === card;
      item.classList.toggle('is-active', isActive);
      var radio = item.querySelector('.productV9OfferRadio');
      if (radio) radio.checked = isActive;
    });

    applyTopPrice(section, getCurrentVariant(section));
    updateCta(section);
  }

  function applyVariant(section, variant) {
    if (!variant) return;

    switchGallery(section, String(variant.id));

    var variantInput = section.querySelector('.productV9VariantInput');
    if (variantInput) variantInput.value = variant.id;

    /* Availability of the page product only gates twelve/onetime; updateCta
       (called from applyOfferTexts) reads it back per active offer. */
    var atcBtn = section.querySelector('.productV9Atc');
    if (atcBtn) atcBtn.dataset.available = variant.available === false ? 'false' : 'true';

    applyOfferAvailability(section, variant);
    applyOfferTexts(section, variant);
    applyTopPrice(section, variant);

    var variantJsonEl = section.querySelector('.productV9VariantJSON');
    if (variantJsonEl) variantJsonEl.textContent = JSON.stringify(variant);

    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  function updateFromSelections(section) {
    var productData = parseProductJSON(section);
    if (!productData) return;

    var flavorCard = section.querySelector('.productV9FlavorCard.is-selected');
    var variant = null;

    if (flavorCard) {
      var directVariantId = parseInt(flavorCard.dataset.variantId, 10);
      if (directVariantId) {
        variant = productData.variants.find(function (item) { return item.id === directVariantId; });
      }
      if (!variant) variant = resolveVariant(productData, flavorCard.dataset.flavorValue || '');
    }

    if (!variant) {
      var variantInput = section.querySelector('.productV9VariantInput');
      var currentId = variantInput ? parseInt(variantInput.value, 10) : null;
      if (currentId) {
        variant = productData.variants.find(function (item) { return item.id === currentId; });
      }
      if (!variant) variant = productData.variants.find(function (item) { return item.available; }) || productData.variants[0];
    }

    applyVariant(section, variant);
  }

  function initFlavorSelector(section) {
    var cards = section.querySelectorAll('.productV9FlavorCard');
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        cards.forEach(function (item) { item.classList.remove('is-selected'); });
        card.classList.add('is-selected');
        updateFromSelections(section);
      });
    });
  }

  function initOfferSelector(section) {
    var select = section.querySelector('.productV9OfferSelect');
    if (!select) return;
    select.addEventListener('change', function () {
      updateFromSelections(section);
    });
  }

  /* Card click / hidden radio → single accordion state. Clicks inside the
     internal selects never collapse the card. The family flavor selects are
     read at ATC time — no side effects on change. */
  function initOfferAccordion(section) {
    section.querySelectorAll('.productV9Offer').forEach(function (card) {
      card.addEventListener('click', function (event) {
        if (event.target.closest('select')) return;
        setActiveOffer(section, card.dataset.offer);
      });
      var radio = card.querySelector('.productV9OfferRadio');
      if (radio) {
        radio.addEventListener('change', function () {
          if (radio.checked) setActiveOffer(section, card.dataset.offer);
        });
      }
    });
  }

  /* ── Family Pack selection + gift payloads ── */

  function getFamilySelection(section) {
    var familyCard = getOfferCard(section, 'family');
    if (!familyCard) return null;

    var slots = [];
    familyCard.querySelectorAll('.productV9FamilyFlavorSelect').forEach(function (select) {
      var option = select.selectedOptions[0] || select.options[0];
      if (!option) return;
      slots.push({
        slot: select.dataset.slot || '',
        variantId: parseInt(option.dataset.variantId, 10) || null,
        flavor: option.value || option.textContent.trim()
      });
    });

    return {
      planId: parseInt(familyCard.dataset.familyPlanId, 10) || null,
      slots: slots
    };
  }

  function giftLinesFor(offerKey) {
    if (typeof giftsV9 === 'undefined') return [];
    return giftsV9.filter(function (gift) {
      if (!parseInt(gift.id, 10)) return false;
      if (offerKey === 'family') return true; /* subscription: full set incl. family-only */
      if (gift.familyOnly) return false;
      if (offerKey === 'twelve') return true; /* subscription: all base gifts */
      return !gift.sub; /* one-time: only gifts not marked subscription-only */
    });
  }

  /* Per-gift dedup (v8 skipped the whole set when the owner had ANY gift):
     a shopper who already owns the base pair via an individual subscription
     still gets the family-only gift when the pack's adult slot reuses the
     same variant. Ownership is tagged with _gift_for so the mini-cart can
     purge gifts when their subscription leaves the cart. */
  function missingGiftLines(cart, ownerVariantId, offerKey) {
    var ownerKey = String(ownerVariantId);
    var cartItems = (cart && cart.items) || [];
    return giftLinesFor(offerKey)
      .filter(function (gift) {
        var giftId = String(parseInt(gift.id, 10));
        return !cartItems.some(function (item) {
          return item.properties && item.properties._gift &&
            String(item.properties._gift_for) === ownerKey &&
            String(item.variant_id) === giftId;
        });
      })
      .map(function (gift) {
        return { quantity: 1, id: parseInt(gift.id, 10), properties: { _gift: true, _gift_for: ownerKey } };
      });
  }

  function initFaqAccordion(section) {
    section.querySelectorAll('.productV9FaqBtn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isActive = btn.classList.contains('is-active');
        section.querySelectorAll('.productV9FaqBtn').forEach(function (item) { item.classList.remove('is-active'); });
        if (!isActive) btn.classList.add('is-active');
      });
    });
  }

  function initLabelDialog(section) {
    var openBtn = section.querySelector('.productV9LabelBtn');
    var dialog = section.querySelector('.productV9LabelDialog');
    if (!openBtn || !dialog) return;

    var labelSwiper = null;
    if (typeof Swiper !== 'undefined') {
      var swiperEl = dialog.querySelector('.productV9LabelSwiper');
      if (swiperEl) {
        labelSwiper = new Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: 20,
          autoHeight: true,
          navigation: {
            prevEl: dialog.querySelector('.productV9LabelNavBtn.swiper-button-prev'),
            nextEl: dialog.querySelector('.productV9LabelNavBtn.swiper-button-next'),
          },
        });
      }
    }

    openBtn.addEventListener('click', function () {
      if (labelSwiper) {
        var variantInput = section.querySelector('.productV9VariantInput');
        var currentId = variantInput ? variantInput.value : null;
        if (currentId) {
          var slides = Array.from(dialog.querySelectorAll('.swiper-slide[data-id]'));
          var idx = slides.findIndex(function (slide) { return slide.dataset.id === String(currentId); });
          if (idx >= 0) labelSwiper.slideTo(idx, 0);
        }
      }
      document.body.classList.add('noScroll');
      dialog.showModal();
    });

    var closeBtn = dialog.querySelector('.productV9LabelDialogClose');
    if (closeBtn) closeBtn.addEventListener('click', function () {
      dialog.close();
      document.body.classList.remove('noScroll');
    });

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) {
        dialog.close();
        document.body.classList.remove('noScroll');
      }
    });

    dialog.addEventListener('close', function () {
      document.body.classList.remove('noScroll');
    });
  }

  function initAtcButton(section) {
    var atcBtn = section.querySelector('.productV9AtcSub');
    if (!atcBtn) return;

    atcBtn.addEventListener('click', async function () {
      if (this.classList.contains('loading')) return;
      this.classList.add('loading');

      var offerKey = getActiveOfferKey(section);
      var items = [];

      try {
        // Keep whatever is already in the cart so different products can coexist.
        // Gifts carry _gift_for ownership so they follow their subscription.
        var currentCart = await getCart();

        if (offerKey === 'family') {
          var family = getFamilySelection(section);
          var slots = family ? family.slots.filter(function (slot) { return slot.variantId; }) : [];

          // Family Pack guard: subscription is mandatory — it has no one-time
          // mode. Without the plan and both slots resolved it must not be added.
          if (!family || !family.planId || slots.length < 2) {
            console.error('Product V9: Family Pack requires a selling plan and both slot variants');
            this.classList.remove('loading');
            return;
          }

          var ownerVariantId = slots[0].variantId;
          items = items.concat(missingGiftLines(currentCart, ownerVariantId, 'family'));
          slots.forEach(function (slot) {
            items.push({
              quantity: 1,
              id: slot.variantId,
              selling_plan: family.planId,
              properties: {
                _family_pack: true,
                _family_pack_flavor: slot.flavor,
                _variety: true,
                _selling_plan_offer: String(family.planId)
              }
            });
          });
        } else {
          var hasSub = offerKey === 'twelve';
          var activeOffer = getActiveOffer(section);
          var planId = hasSub && activeOffer ? (parseInt(activeOffer.value, 10) || null) : null;
          var quantity = activeOffer ? (parseInt(activeOffer.dataset.quantity, 10) || 1) : 1;
          var variantInput = section.querySelector('.productV9VariantInput');
          var variantId = variantInput ? parseInt(variantInput.value, 10) : null;

          items = items.concat(missingGiftLines(currentCart, variantId, offerKey));
          items.push({
            quantity: quantity,
            id: variantId,
            selling_plan: planId,
            properties: hasSub && planId ? { _selling_plan_offer: planId } : {}
          });
        }

        await addProduct({ items: items });
      } catch (error) {
        console.error('Product V9 add to cart failed', error);
      }

      this.classList.remove('loading');
      await openCart();
    });
  }

  function initGiftDialogs(section) {
    section.querySelectorAll('.productV9SubPointLearnBtn').forEach(function (btn) {
      var item = btn.closest('.productV9SubPointItem');
      var dialog = item ? item.querySelector('.productV9SubPointLearnDialog') : null;
      if (!dialog) return;

      btn.addEventListener('click', function () { dialog.showModal(); });
      dialog.querySelectorAll('.productV9SubPointLearnClose').forEach(function (closeBtn) {
        closeBtn.addEventListener('click', function () { dialog.close(); });
      });
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.detail && event.detail.sectionId === sectionId) init();
  });

  function init() {
    var section = getSection();
    if (!section) return;

    initGallerySwiper(section);
    initFlavorSelector(section);
    initOfferSelector(section);
    initOfferAccordion(section);
    initAtcButton(section);
    initFaqAccordion(section);
    initLabelDialog(section);
    initGiftDialogs(section);
    updateFromSelections(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
